import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import TopHeader from '../../template/TopHeader';
import MarketingTopNav from '../MarketingTopNav';
import MarketingCalendar from '../../marketing-v2/features/calendar/components/MarketingCalendar';
import PostStudio from '../../marketing-v2/features/social/components/PostStudio';
import { useSalonBrand } from '../../marketing-v2/features/brand/useSalonBrand';
import { marketFor, normHex } from '../../marketing-v2/features/email-composer/email/theme';
import { townOf } from '../../marketing-v2/features/social/data/captions';
import { familyForLook } from '../../marketing-v2/features/social/render/families';
import { salonLinks } from '../../marketing-v2/features/email-composer/email/links';
import { usePlans } from '../../marketing-v2/features/plans/usePlans';
import { planSuggestions } from '../../marketing-v2/features/plans/data/planner';
import { campaignBands } from '../../marketing-v2/features/calendar/data/calendarData';
import { planPath, campaignPath } from '../../marketing-v2/features/campaigns/data/open';
import { useMarketingControl } from '../SalonRunsMarketing';
import { MARKETING_HOME_VISIBLE } from '../../../config/featureFlags';
import '../campaigns/Campaigns.css';
import '../../marketing-v2/features/social/Social.css';

/**
 * Marketing › Calendar
 * Everything marketing sends or posts, by day: social posts, email and SMS
 * campaigns, and the UK dates worth planning for. Month or week on a
 * computer, the week as a list on a phone.
 */
function MarketingCalendarPage(props) {
  const brand = useSalonBrand();
  const [studio, setStudio] = useState(null);
  const salon = brand.salon;
  const hairdresser = (props.user && props.user.userInfo && props.user.userInfo.hairdresser) || {};
  const trade = marketFor(salon && salon.businessType) || marketFor(hairdresser.salon && hairdresser.salon.businessType) || 'hair';
  const salonName = brand.spec.name || (salon && salon.salonName) || '';
  const brandColour = normHex(brand.spec.accent) || null;
  const kit = useMemo(() => ({ name: salonName || 'Your salon', accent: brandColour, logo: brand.spec.logo || null, display: brand.spec.display || null, text: brand.spec.text || null }), [salonName, brandColour, brand.spec.logo, brand.spec.display, brand.spec.text]);
  const control = useMarketingControl();
  const country = String((hairdresser.salon && (hairdresser.salon.countryShortName || hairdresser.salon.country)) || 'GB').toUpperCase().slice(0, 2);
  const family = familyForLook(brand.spec.look);
  const ctx = useMemo(() => ({ market: trade, kit: brand.spec, links: salonLinks(hairdresser), salonName }), [trade, brand.spec, salonName]); // eslint-disable-line react-hooks/exhaustive-deps
  const { plans, planForDay, campaigns } = usePlans({ dispatch: props.dispatch, user: props.user, trade, salonName, brandColour, family, country, links: ctx.links, currency: hairdresser.currencySymbol || '£', enabled: MARKETING_HOME_VISIBLE && !control.managed });
  const town = townOf(salon && salon.formattedAddress);
  // Plans open in the Campaign Studio, which brings them back here on close
  const review = (plan) => { if (plan) props.history.push(planPath(plan), { from: '/marketing-calendar' }); };
  if (!MARKETING_HOME_VISIBLE) return <Redirect to="/marketing-campaigns" />;
  return (
    <div className="h_n catalogue-no-header">
      <div className="body_container cm_bg_white">
        <TopHeader title="" {...props} />
        <div className="catalogue-page-wrapper">
          <MarketingTopNav activeTab="calendar" />
          <div className="catalogue-content-container">
            <div className="cmp-wrap">
              <div className="cmp-head">
                <div className="cmp-head-text">
                  <h1 className="cmp-title">Calendar</h1>
                  <p className="cmp-subtitle">Posts, emails and texts, and the dates worth planning for, all in one place.</p>
                </div>
              </div>
              <MarketingCalendar
                mode="full"
                title=""
                dispatch={props.dispatch}
                trade={trade}
                kit={kit}
                plans={control.managed ? [] : planSuggestions(plans)}
                onOpenPlan={(it) => review(plans.find((p) => p.key === it.planKey) || null)}
                bands={campaignBands({ campaigns, plans: control.managed ? [] : plans })}
                onOpenBand={(b) => {
                  if (b.planKey) review(plans.find((p) => p.key === b.planKey) || null);
                  else props.history.push(campaignPath(b.campaignId), { from: '/marketing-calendar' });
                }}
                createKinds={control.managed ? ['post'] : ['post', 'email', 'sms', 'bailey']}
                onOpenPost={(p) => setStudio(p)}
                onOpenCampaign={(item) => props.history.push('/marketing-campaigns', { openCampaignId: item.id })}
                onCreate={(day, kind) => {
                  if (kind === 'bailey') review(planForDay(day));
                  else if (kind === 'post') props.history.push('/marketing-social', { create: true, day: day.toISOString() });
                  else props.history.push('/marketing-campaigns', { compose: kind });
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {studio && <PostStudio post={studio} kit={kit} trade={trade} salonName={salonName} town={town} salon={salon} onClose={() => setStudio(null)} />}
    </div>
  );
}

function mapStateToProps(state) {
  return { user: state.user };
}

export default connect(mapStateToProps)(MarketingCalendarPage);
